import React, { useEffect } from 'react';
import {
  DatePicker,
  DatePickerInput,
  Dropdown,
  MultiSelect,
  TextInput,
  NumberInput
} from 'carbon-components-react';
import { FilterInputProps } from '../../types';

/**
 * FilterInput component.
 *
 * Renders the appropriate input control based on filter type.
 * Supports both static options (from filter.options) and dynamic
 * options loaded via SDK useFilterOptions hook.
 */
export const FilterInput: React.FC<FilterInputProps> = ({
  filter,
  value,
  error,
  onChange,
  onBlur,
  options = [],
  optionsLoading = false,
  disabled = false
}) => {
  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  /**
   * Auto-select first item for select filters when no value is set.
   *
   * WHY: Provides a better UX by pre-selecting a value instead of showing
   * a placeholder, reducing the number of clicks needed.
   *
   * HOW: Items are sorted alphabetically unless preserveOrder is true
   * (e.g. for chronological month options).
   */
  useEffect(() => {
    if (filter.type === 'select') {
      const sourceItems = options.length > 0 ? options : filter.options ?? [];
      const rawItems = sourceItems.slice();
      const items = filter.preserveOrder
        ? rawItems
        : rawItems.sort((a, b) => a.label.localeCompare(b.label));
      if (!value && items.length > 0 && !optionsLoading) {
        onChange(items[0].value);
      }
    }
  }, [
    filter.type,
    filter.options,
    filter.preserveOrder,
    options,
    value,
    optionsLoading,
    onChange
  ]);

  // Date input
  if (filter.type === 'date') {
    // Convert YYYY-MM-DD string to DD/MM/YYYY format for DatePicker
    const formatDateForPicker = (dateStr: string) => {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    };

    // Convert MM/DD/YYYY to YYYY-MM-DD for API
    const formatDateForAPI = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return (
      <DatePicker
        datePickerType="single"
        dateFormat="d/m/Y"
        onChange={(dates: Date[]) => {
          if (dates && dates.length > 0) {
            const dateStr = formatDateForAPI(dates[0]);
            onChange(dateStr);
          } else {
            onChange('');
          }
        }}
      >
        <DatePickerInput
          id={`filter-${filter.id}`}
          placeholder="dd/mm/yyyy"
          labelText={filter.name}
          invalid={!!error}
          invalidText={error}
          onBlur={handleBlur}
          value={formatDateForPicker(String(value || ''))}
          disabled={disabled}
        />
      </DatePicker>
    );
  }

  // Select (single)
  if (filter.type === 'select') {
    // Use provided options (from SDK) or fall back to filter.options
    // Sort items alphabetically unless preserveOrder is true (e.g. for chronological options)
    const sourceItems = options.length > 0 ? options : filter.options ?? [];
    const rawItems = sourceItems.slice();
    const items = filter.preserveOrder
      ? rawItems
      : rawItems.sort((a, b) => a.label.localeCompare(b.label));
    const selectedItem = items.find((item) => item.value === value) || null;

    return (
      <Dropdown
        id={`filter-${filter.id}`}
        titleText={filter.name}
        label={
          optionsLoading ? 'Loading...' : filter.placeholder || 'Select...'
        }
        items={items}
        itemToString={(item) => (item ? item.label.toUpperCase() : '')}
        selectedItem={selectedItem}
        onChange={({ selectedItem: selected }) => {
          onChange(selected ? selected.value : '');
        }}
        invalid={!!error}
        invalidText={error}
        onBlur={handleBlur}
        disabled={disabled || optionsLoading}
      />
    );
  }

  // Multi-select
  if (filter.type === 'multiSelect') {
    // Use provided options (from SDK) or fall back to filter.options
    // Sort items alphabetically unless preserveOrder is true (e.g. for chronological options)
    const sourceItems = options.length > 0 ? options : filter.options ?? [];
    const rawItems = sourceItems.slice();
    const items = filter.preserveOrder
      ? rawItems
      : rawItems.sort((a, b) => a.label.localeCompare(b.label));
    const currentValue = Array.isArray(value) ? value : [];
    const selectedItems = items.filter((item) =>
      currentValue.includes(item.value)
    );

    // Force re-render when options change by using a key
    const multiSelectKey = `${filter.id}-${items.length}`;

    return (
      <MultiSelect
        key={multiSelectKey}
        id={`filter-${filter.id}`}
        titleText={filter.name}
        label={
          optionsLoading
            ? 'Loading...'
            : filter.placeholder || 'Select items...'
        }
        items={items}
        itemToString={(item: { value: string; label: string } | null) =>
          item ? item.label : ''
        }
        initialSelectedItems={selectedItems}
        onChange={({
          selectedItems: selected
        }: {
          selectedItems: Array<{ value: string; label: string }>;
        }) => {
          const values = selected.map((item) => item.value);
          onChange(values);
        }}
        invalid={!!error}
        invalidText={error}
        disabled={disabled || optionsLoading}
      />
    );
  }

  // Number input
  if (filter.type === 'number') {
    const numValue: number | '' =
      value !== null && value !== undefined && value !== ''
        ? Number(value)
        : '';

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      onChange(val === '' ? '' : Number(val));
    };

    return (
      <NumberInput
        id={`filter-${filter.id}`}
        label={filter.name}
        value={numValue}
        placeholder={filter.placeholder}
        onChange={handleNumberChange}
        invalid={!!error}
        invalidText={error}
        onBlur={handleBlur}
        min={filter.validation?.min}
        max={filter.validation?.max}
        disabled={disabled}
      />
    );
  }

  // Text input (default)
  const textValue = value ? String(value) : '';
  return (
    <TextInput
      id={`filter-${filter.id}`}
      labelText={filter.name}
      value={textValue}
      placeholder={filter.placeholder}
      onChange={(e: any) => {
        onChange(e.target.value);
      }}
      invalid={!!error}
      invalidText={error}
      onBlur={handleBlur}
      disabled={disabled}
    />
  );
};

export default React.memo(FilterInput);
